import PageTransition from '../components/PageTransition';
import ScrollReveal from '../components/ScrollReveal';
import ServiceCard from '../components/ServiceCard';
import CTASection from '../components/CTASection';
import { services } from '../data/services';
import { useSEO } from '../hooks/useSEO';
import { PAGE_SEO } from '../utils/seo';

const processSteps = [
  {
    number: '01',
    title: 'Discover',
    description: 'We immerse ourselves in your world — your audience, your competitors, your ambitions. Every great project starts with deep understanding.',
  },
  {
    number: '02',
    title: 'Define',
    description: 'We distill research into a clear creative direction. Three distinct routes, not one safe option. Real choices, not an illusion of choice.',
  },
  {
    number: '03',
    title: 'Create',
    description: 'This is where the work happens. We build, iterate, and refine with relentless attention to every pixel, frame, and interaction.',
  },
  {
    number: '04',
    title: 'Deliver',
    description: 'We hand over production-ready assets, comprehensive guidelines, and everything your team needs to carry the work forward.',
  },
];

export default function ServicesPage() {
  // Apply SEO
  useSEO(PAGE_SEO.services);
  return (
    <PageTransition>
      {/* Hero */}
      <section className="section">
        <div className="container">
          <ScrollReveal>
            <h1 className="h-1" style={{ marginBottom: 'var(--space-4)' }}>
              What We Do
            </h1>
            <p className="body-lg" style={{ color: 'var(--text-secondary)', maxWidth: '640px' }}>
              End-to-end creative production from concept to delivery.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section">
        <div className="container">
          <div className="grid-3">
            {services.map((service, i) => (
              <ScrollReveal key={service.id} delay={i * 80}>
                <ServiceCard service={service} variant="default" />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service Breakdowns */}
      <section className="section" style={{ borderTop: '1px solid var(--separator)' }}>
        <div className="container">
          <ScrollReveal>
            <h2 className="h-2" style={{ marginBottom: 'var(--space-3)' }}>
              Service Details
            </h2>
            <p className="body" style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-10)', maxWidth: '560px' }}>
              Deep dive into what we offer and why we're good at it.
            </p>
          </ScrollReveal>

          {services.map((service, i) => (
            <ScrollReveal key={service.id} delay={i * 60}>
              <div className="service-detail card" style={{ marginBottom: 'var(--space-6)', padding: 'var(--space-8)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: service.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {service.icon === 'android' && <><rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></>}
                      {service.icon === 'web' && <><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>}
                      {service.icon === 'ecommerce' && <><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></>}
                      {service.icon === 'branding' && <><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></>}
                      {service.icon === 'reddit' && <><path d="M4 4h16v12a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V4z" /><circle cx="9" cy="11" r="1.5" fill="currentColor" /><circle cx="15" cy="11" r="1.5" fill="currentColor" /><path d="M10 14c1 1 4 1 4 0" /></>}
                      {service.icon === 'design' && <><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /><circle cx="11" cy="11" r="2" /></>}
                      {service.icon === 'strategy' && <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></>}
                      {service.icon === 'support' && <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></>}
                      {service.icon === 'privacy' && <><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>}
                      {service.icon === 'game' && <><line x1="6" y1="12" x2="10" y2="12" /><line x1="8" y1="10" x2="8" y2="14" /><circle cx="15" cy="13" r="1" /><circle cx="18" cy="11" r="1" /><rect x="2" y="6" width="20" height="12" rx="2" /></>}
                      {service.icon === 'performance' && <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></>}
                      {service.icon === 'store' && <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>}
                      {service.icon === 'mobile' && <><rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></>}
                      {service.icon === 'desktop' && <><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></>}
                      {service.icon === 'content' && <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>}
                    </svg>
                  </div>
                  <h3 className="h-3" style={{ margin: 0 }}>{service.title}</h3>
                </div>

                <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-5)' }}>
                  {service.longDescription}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
                  <div>
                    <h4 className="caption-bold" style={{ color: 'var(--accent-green)', marginBottom: 'var(--space-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      What&apos;s Included
                    </h4>
                    <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0 }}>
                      {service.features.map((feature) => (
                        <li
                          key={feature}
                          className="caption"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            color: 'var(--text-secondary)',
                            marginBottom: 'var(--space-2)',
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--accent-green)"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ flexShrink: 0 }}
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="caption-bold" style={{ color: 'var(--text-warning)', marginBottom: 'var(--space-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Why We Excel
                    </h4>
                    <p className="caption" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                      {service.whyWeExcel}
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <h2 className="h-2" style={{ marginBottom: 'var(--space-3)' }}>
              How We Work
            </h2>
            <p className="body" style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-10)', maxWidth: '560px' }}>
              Every project follows a proven four-phase process — disciplined enough to deliver consistently, flexible enough to stay creative.
            </p>
            <div className="grid-2">
              {processSteps.map((step, i) => (
                <ScrollReveal key={step.number} delay={i * 100}>
                  <div className="process-step card" style={{ display: 'flex', gap: 'var(--space-5)', padding: 'var(--space-6)' }}>
                    <span
                      className="process-step__number h-2"
                      style={{
                        color: 'var(--accent-green)',
                        fontFamily: 'var(--font-title)',
                        fontWeight: 800,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      {step.number}
                    </span>
                    <div>
                      <h3 className="h-4" style={{ marginBottom: 'var(--space-2)' }}>
                        {step.title}
                      </h3>
                      <p className="caption" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            <CTASection
              title="Need something specific?"
              subtitle="Every project is different. Tell us what you need and we'll figure out the rest."
              primaryLabel="Let's Talk"
              secondaryLabel="View Our Work"
              primaryHref="/contact"
              secondaryHref="/work"
            />
          </div>
        </section>
      </ScrollReveal>
    </PageTransition>
  );
}
